/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2018 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
import React from 'react';
import { Page, withModel } from '@adobe/cq-react-editable-components';
import { connect } from 'react-redux';
import { compose } from 'redux';
import loadjs from 'loadjs';
import withRouterV6 from '../hoc/withRouterV6';

import ErrorBoundary from '../common/ErrorBoundary';
import SessionTimeOut from '../common/SessionModal';
import { getAuthDomain } from '../../services/UrlMapperService';
import { getSession, isMVA, setSession, viewport, getAPPID, isVFEligibile, isWWM, isMoversFlow } from '../common/Helpers/index';
import { postMessage } from '../common/Helpers/app';
import { apiUrls } from '../../constants/apiUrl';
import { ContentSquareInit } from '../common/Helpers/cs';
import { Polymap } from '../common/Helpers/Polymap';
import MultipleSessionsModal from '../Web/Cart/MultipleSessionsModal';

require('../../assets/fonts/verizon-fonts/fonts.css');
require('./App.css');
require('../../assets/css/layout.scss');
require('../../assets/css/singles.css');

// This component is the application entry point

class App extends Page {
  constructor(props) {
    super(props);
    // check for complexity
    if (props.apiServer && props?.localStorage === true) {
      window.localStorage.setItem('APIContext', props.apiServer);
    } else if (props.apiServer) {
      setSession('APIContext', props.apiServer);
    }

    let amAgentLastCachedTime = getSession('amAgentLastCachedTime');
    if (!amAgentLastCachedTime) {
      amAgentLastCachedTime = new Date().getTime();
    }

    const appId = getAPPID();

    window.vzFlags = {
      amAgentLastCachedTime,
      appId: appId || 'NSE',
      isLoggedIn: appId === 'AAL',
      isProd: props.isProd === 'true',
      colorTheme: props.colorTheme || '',
      whwPlusName: props.whwPlusName || 'Whole-Home Wi-Fi Plus',
      whwBasicName: props.whwBasicName || 'Whole-Home Wi-Fi',
      extendertext: props.extendertext || 'Wi-Fi Extender',
      localStorage: props.localStorage,
      enableVideoCall: props.enableVideoCall,
      disableChatLinks: props.disableChatLinks,
      shippingGetCheckoutNSE: props.shippingGetCheckoutNSE || false,
      showVZVisaCard: props.showVZVisaCard || false,
      isInvalidUnitNum: props.isInvalidUnitNum || false,
      getCheckOutDetails: props.getCheckOutDetails || false,
      isSkipMtnApi: props.isSkipMtnApi || false,
      acpTagging: props.acpTagging,
      isWHWISPU: props.isWHWISPU,
      isWHWPhase2: props.isWHWPhase2,
      whwBasicQuantity: props.whwBasicQuantity,
      whwUpgradeOnly: props.whwUpgradeOnly,
      whwPlusQuantity: props.whwPlusQuantity,
      isWHWPhase2Term: props.isWHWPhase2Term,
      isWHWPCartRestart: props.isWHWPCartRestart,
      localOfferFlag: props.localOfferFlag,
      excludeIndirect: props.excludeIndirect,
      issetupEdit: props.issetupEdit,
      registrationTimeoutNonProd: props.registrationTimeoutNonProd || '25000',
      registrationTimeoutProd: props.registrationTimeoutProd || '300000',
      pdcFlag: props.pdcFlag || false,
      fwaModalFlag: props.fwaModalFlag,
      modalFwaTitle: props.modalFwaTitle,
      fwaModalDesktopTimeout: props.fwaModalDesktopTimeout,
      fwaModalMobileTimeout: props.fwaModalMobileTimeout,
      fwaModalMobileImgUrl: props.fwaModalMobileImgUrl,
      fwaModalDesktopImgUrl: props.fwaModalDesktopImgUrl,
      invalidCaseStatusCode: props.invalidCaseStatusCode || '1008,1007,1000',
      msgwithLinkErrorCode: props.msgwithLinkErrorCode || '06, 400,615,701,1000,1008,1023,3000,3002,3504,3606,3607,15501,17104',
      isValidateCartCall: props.isValidateCartCall,
      miniCartApi: props.miniCartApi,
      isLoader: props.isLoader === 'true',
      enableAPFuncComp: props.enableAPFuncComp || false,
      acpIntegrated: props.acpIntegrated,
      vfIntegrated: props.vfIntegrated,
      vfNyIntegrated: props.vfNyIntegrated,
      plansVisitLq: props.plansVisitLq,
      isBroadBandFacts: props.isBroadBandFacts,
      nonVzConsent_dark: props.nonVzConsent_dark,
      lteHomePlus: props.lteHomePlus,
      lteHome: props.lteHome,
      fiveGHomePlus: props.fiveGHomePlus,
      fiveGHome: props.fiveGHome,
      isUnitTypeAhead: props.isUnitTypeAhead,
      isOnlyTypeAhead: props.isOnlyTypeAhead,
      useTypeAheadSoeUrl: props.useTypeAheadSoeUrl,
      integratedCartContactInfo: props.integratedCartContactInfo,
      iscontactButtonEnabled: props.iscontactButtonEnabled,
      enablePegaProcessStep: props.enablePegaProcessStep,
      expressCheckoutPegaStep: props.expressCheckoutPegaStep,
      enablePlanInfoAPI: props.enablePlanInfoAPI,
      removeLQResponse: props.removeLQResponse,
      isEPC: props.isEPC,
      isSPARedirectionEnabled: props.isSPARedirectionEnabled || false,
      isDiscountEnabled: props.isDiscountEnabled,
      isSalesApproach: props.isSalesApproach,
      isYoutubePremiumEnabled: props.isYoutubePremiumEnabled,
      enableCookieKeys: props.enableCookieKeys,
      prefetchAddons: props.prefetchAddons,
      fetchAddOns: props.fetchAddOns,
      prefetchGetShipping: props.prefetchGetShipping,
      addPlanOnPlanSelection: props.addPlanOnPlanSelection,
      shellFwaAutoPay: false,
      isCartPromos: props.isCartPromos || false,
      isSigninLink: props.isSigninLink || false,
      isSigninPrompt: props.isSigninPrompt || false,
      enableDisneyBundlePromo: props.enableDisneyBundlePromo || false,
      redirectToPerks: props.redirectToPerks || false,
      disableLocalStoargeUpdate: props.disableLocalStoargeUpdate || false,
      isDigital: props.isDigital || true,
      isMfeNbs: props.isMfeNbs || false,
      enableProgressivePlansLoader: props.enableProgressivePlansLoader || false,
      prefetchGetCheckoutAndSms: props.prefetchGetCheckoutAndSms || false,
      freePROSetup: props.freePROSetup || false,
      autoPaySubtext: props.autoPaySubtext,
      autoPayBannerText: props.autoPayBannerText,
      autoPayBannerTooltipText: props.autoPayBannerTooltipText,
      isAccountOwnerEmailEnable: props.isAccountOwnerEmailEnable || false,
      isPZAIEnable: props.isPZAIEnable || false,
      isPolymapEnabled: props.isPolymapEnabled || false,
      isContinueNseEnable: props.isContinueNseEnable || false,
      isFlowNameEnabled: props.isFlowNameEnabled || false,
      wifiBackUpBanner: props.wifiBackUpBanner,
      selfSetupBundleId: props.selfSetupBundleId,
    };

    window.redirectToHomePage = () => {
      window.sessionStorage.clear();
      window.localStorage.clear();
      setSession('isTransExpiryRetry', 'Y');
      window.location.href = `${apiUrls().redirect5Ghome}?retryFlow=true`;
    };
    window.redirectToPaymentPage = () => {
      setSession('isPaymentFailRetry', 'Y');
      window.location.href = `${apiUrls().redirectPayment}?retryFlow=true`;
    };
    if (getSession('enablePolyMap') === 'Y') {
      loadjs('https://code.jquery.com/jquery-3.6.1.min.js', { returnPromise: true })
        .then(() => {
          Polymap();
        })
        .catch(() => {});
    }
  }

  // eslint-disable-next-line class-methods-use-this
  componentCleanup() {
    if (window?.vzFlags?.localStorage) {
      localStorage.clear();
    }
    sessionStorage.clear();
  }

  componentDidMount() {
    // Safari/iOS aggressively restores pages from the back-forward cache (bfcache) on
    // browser back/forward navigation, so lifecycle methods like componentDidMount never
    // re-run and API calls made on the initial mount are not re-fired. Force a fresh
    // reload in that case so the page re-fetches data instead of showing a stale state.
    window.addEventListener('pageshow', this.handleBfCacheRestore);
  }

  componentWillUnmount() {
    this.componentCleanup();
    window.removeEventListener('beforeunload', this.componentCleanup);
    window.removeEventListener('pageshow', this.handleBfCacheRestore);
  }

  // eslint-disable-next-line class-methods-use-this
  handleBfCacheRestore = (event) => {
    if (event?.persisted) {
      window.location.reload();
    }
  };

  showFabIcon = () => {
    const msg = {
      actionType: 'updateChatFab',
      chatFab: {
        hideFabOverlay: true,
        shouldExpandFab: true,
      },
    };
    postMessage(msg);
  };

  render() {
    if (window && (window.vzwDL === undefined || window.vzwDL === null)) {
      window.vzwDL = {};
    }

    let sessionTimeOutJSON = {
      timeout: this.props.sessionTimeoutTime || '15',
      warning: this.props.sessionTimeoutWarningTime || '12',
      newSessionTimer: this.props.newSessionTimer || false,
      amAgentLastCachedTime: window.vzFlags.amAgentLastCachedTime,
      continueSessionURL: window.location.href,
      guestContinueSessionURL: window.location.href,
      ctxText: 'Back to 5G Home',
    };
    if (isWWM()) {
      sessionTimeOutJSON = {
        ...sessionTimeOutJSON,
        ctxText: 'Back to Landing page',
        landingPageUrl: '/digital/nsa/secure/ui/udb/#/?es=y',
      };
    } else {
      let landingPageUrl = '/5g/home/?es=y';
      if (isVFEligibile()) {
        landingPageUrl = '/discounts/verizon-forward/';
      } else if (isMoversFlow()) {
        landingPageUrl = `https://${getAuthDomain()}/signin/user/logout?next=https%3A%2F%2F${window.location.hostname}%2Fhome%2Finternet%3Ftype%3Dmoving%26auth%3Dy`;
      }
      sessionTimeOutJSON = {
        ...sessionTimeOutJSON,
        landingPageUrl,
      };
    }

    this.showFabIcon();

    const type = viewport();
    let classValue = '';
    if (type === 'mobile') {
      classValue = 'iteMobile foo-content-container-min-height';
    } else if (type === 'tablet') {
      classValue = 'tabletDevice foo-content-container-min-height';
    } else if (type === 'desktop') {
      classValue = 'foo-content-container-min-height';
    }
    classValue += ` ${isMVA() ? 'mvaDevice' : ''}`;
    return (
      <ErrorBoundary>
        <div className={classValue}>
          <ContentSquareInit />
          {this.childComponents}
          {this.childPages}
          {type !== 'mfapp' && sessionTimeOutJSON && Object.keys(sessionTimeOutJSON).length > 0 && (
            <SessionTimeOut {...sessionTimeOutJSON} timerToggleFlag={this.props.timerToggleFlag} />
          )}
          {this.props.showDeviceLockedModal && <MultipleSessionsModal isOpen={this.props.showDeviceLockedModal} />}
        </div>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { preLoadServices, completedRequests } = state.loader || {};
  const { asyncRequestWatcher } = ownProps;
  let preLoadServiceCompleted = true;
  if (asyncRequestWatcher && preLoadServices?.length) {
    const completedRequestKeys = Object.keys(completedRequests || {}).filter((requestKey) => completedRequests[requestKey]);
    preLoadServiceCompleted = preLoadServices.every((preLoadRequest) => completedRequestKeys.includes(preLoadRequest));
  }
  return {
    loaderStatus: (state && state.loader && state.loader.loaderStatus) || !preLoadServiceCompleted,
    timerToggleFlag: state?.sessionExtension?.timerToggleFlag || false,
    showDeviceLockedModal: state.showDeviceLockedModal?.showLockedDeviceModal,
  };
};

const mapDispatchToProps = {
  // fetchURL
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withModel, withConnect, withRouterV6)(App);
